import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

const endpoint = `${process.env.REACT_APP_API_BASE_URL || ''}/api/privacy/requests/`;
export default function PrivacyRequestPage({
  review = false,
}: {
  review?: boolean;
}) {
  const [available, setAvailable] = useState(review);
  const [loading, setLoading] = useState(!review);
  const [scope, setScope] = useState('own');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const [receipt, setReceipt] = useState('');
  useEffect(() => {
    if (review) return;
    let active = true;
    fetch(endpoint, { credentials: 'omit', referrerPolicy: 'no-referrer' })
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((data) => {
        if (active) setAvailable(data.available === true);
      })
      .catch(() => {
        if (active) setAvailable(false);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [review]);
  async function submit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setMessage('');
    if (review) {
      setMessage(
        'Review only. No request has been submitted. Monitoring must be verified before this form is enabled.',
      );
      return;
    }
    const form = e.currentTarget;
    const data = Object.fromEntries(new FormData(form).entries());
    setBusy(true);
    try {
      const r = await fetch(endpoint, {
        method: 'POST',
        credentials: 'omit',
        referrerPolicy: 'no-referrer',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
      const result = await r.json();
      if (!r.ok)
        throw new Error(
          r.status === 429
            ? 'Please wait before trying again.'
            : 'We could not save your request. Please try again.',
        );
      setReceipt(result.reference);
      form.reset();
    } catch (e) {
      setMessage((e as Error).message);
    } finally {
      setBusy(false);
    }
  }
  const cls =
    'mt-1 block w-full rounded border border-gray-400 p-3 text-gray-900';
  return (
    <main className="mx-auto max-w-2xl px-5 py-10 text-gray-900">
      <h1 className="text-3xl font-semibold">Privacy requests</h1>
      {review && (
        <p
          role="note"
          className="my-4 rounded border border-amber-400 bg-amber-50 p-4"
        >
          Review draft · Intake is not enabled. No real information should be
          entered.
        </p>
      )}
      <p className="my-4">
        You do not need an account or subscription. Tell us whether your request
        concerns AskArthur’s own processing, a business interaction, or both.
      </p>
      <p className="my-4">
        We must verify the relevant interaction before disclosing records.
        Current control of a phone number alone does not establish a right to
        historical records. Do not send identity documents, card details,
        recordings or information about other guests.
      </p>
      <p className="my-4">
        A privacy request does not opt you out of SMS, cancel an order or
        reservation, or cancel a business subscription. For SMS opt-out, reply
        STOP to the sender. Contact the business to cancel a transaction.
      </p>
      {loading ? (
        <p role="status">Checking request availability…</p>
      ) : receipt ? (
        <p role="status">
          Request received. Keep reference {receipt}. No records have been
          disclosed or deleted.
        </p>
      ) : !available ? (
        <p role="alert" className="my-6 rounded border p-4">
          Online privacy request intake is not currently available. No request
          has been saved.
        </p>
      ) : (
        <form onSubmit={submit} className="space-y-5">
          <label className="block">
            Reply email
            <input
              className={cls}
              type="email"
              name="contact_email"
              maxLength={254}
              required
              autoComplete="email"
            />
          </label>
          <label className="block">
            Processing involved
            <select
              className={cls}
              name="scope"
              value={scope}
              onChange={(e) => setScope(e.target.value)}
            >
              <option value="own">AskArthur account, website or support</option>
              <option value="guest">Restaurant or hotel interaction</option>
              <option value="both">Both</option>
            </select>
          </label>
          {scope !== 'own' && (
            <label className="block">
              Business, location and approximate interaction date
              <input
                className={cls}
                name="business_context"
                required
                maxLength={300}
              />
            </label>
          )}
          <label className="block">
            Request type
            <select className={cls} name="request_type">
              {[
                'access',
                'correction',
                'deletion',
                'objection',
                'restriction',
                'portability',
                'appeal',
                'complaint',
                'other',
              ].map((v) => (
                <option key={v} value={v}>
                  {v[0].toUpperCase() + v.slice(1)}
                </option>
              ))}
            </select>
          </label>
          <label className="block">
            Country and state or province (optional)
            <input className={cls} name="region" maxLength={100} />
          </label>
          <label className="block">
            Brief description
            <textarea
              className={cls}
              name="details"
              rows={4}
              required
              maxLength={2000}
            />
          </label>
          <p className="text-sm">
            These details are used to review and respond to your request.{' '}
            <Link className="underline" to="/legal/privacy">
              Privacy Policy
            </Link>
            . No marketing consent is requested.
          </p>
          {message && <p role="alert">{message}</p>}
          <button
            disabled={busy}
            className="rounded bg-gray-900 px-5 py-3 text-white disabled:opacity-50"
          >
            {review
              ? 'Check review form'
              : busy
                ? 'Submitting…'
                : 'Submit privacy request'}
          </button>
        </form>
      )}
      <p className="mt-8">
        <Link className="underline" to="/legal/privacy">
          Privacy Policy
        </Link>{' '}
        ·{' '}
        <Link className="underline" to="/legal/archive/privacy-2025-09-18">
          Prior policy
        </Link>
      </p>
    </main>
  );
}
